import { DayOfWeek } from '@wix/restaurants-olo-operations-client-commons';
import type { DateTime } from 'luxon';

import { getMinutesFromTimeOfDay, type TimeOfDay } from '../time';

import type { WeeklyScheduleOptions } from './availabilityTypes';

type AvailabilityTimeRange = {
  start?: TimeOfDay | null;
  end?: TimeOfDay | null;
};

const DayRangeMatch = {
  CurrentDay: 'current-day',
  PreviousDay: 'previous-day',
} as const;

type DayRangeMatchValue = (typeof DayRangeMatch)[keyof typeof DayRangeMatch];

const weeklyAvailabilityDayByLuxonWeekday: Record<number, DayOfWeek> = {
  1: DayOfWeek.MONDAY,
  2: DayOfWeek.TUESDAY,
  3: DayOfWeek.WEDNESDAY,
  4: DayOfWeek.THURSDAY,
  5: DayOfWeek.FRIDAY,
  6: DayOfWeek.SATURDAY,
  7: DayOfWeek.SUNDAY,
};

const availableTimesDayByLuxonWeekday: Record<number, string> = {
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
  7: 'SUN',
};

export const weeklyAvailabilityDayByAvailableTimesDay: Record<
  string,
  DayOfWeek
> = {
  MON: DayOfWeek.MONDAY,
  TUE: DayOfWeek.TUESDAY,
  WED: DayOfWeek.WEDNESDAY,
  THU: DayOfWeek.THURSDAY,
  FRI: DayOfWeek.FRIDAY,
  SAT: DayOfWeek.SATURDAY,
  SUN: DayOfWeek.SUNDAY,
};

const getPreviousLuxonWeekday = (weekday: number) =>
  weekday === 1 ? 7 : weekday - 1;

const isTimeWithinAvailabilityRange = (
  timeOfDayMinutes: number,
  range: AvailabilityTimeRange,
  dayRangeMatch: DayRangeMatchValue,
) => {
  if (!range.start || !range.end) {
    return false;
  }

  const startMinutes = getMinutesFromTimeOfDay(range.start);
  const endMinutes = getMinutesFromTimeOfDay(range.end);

  if (startMinutes <= endMinutes) {
    return (
      dayRangeMatch === DayRangeMatch.CurrentDay &&
      timeOfDayMinutes >= startMinutes &&
      timeOfDayMinutes <= endMinutes
    );
  }

  return dayRangeMatch === DayRangeMatch.CurrentDay
    ? timeOfDayMinutes >= startMinutes
    : timeOfDayMinutes <= endMinutes;
};

const isAvailableForAvailableTimesDay = ({
  weeklyScheduleOptions,
  dayOfWeek,
  timeOfDayMinutes,
  dayRangeMatch,
}: {
  weeklyScheduleOptions: WeeklyScheduleOptions | undefined;
  dayOfWeek: string;
  timeOfDayMinutes: number;
  dayRangeMatch: DayRangeMatchValue;
}) => {
  const daySchedule = weeklyScheduleOptions?.availableTimes?.find(
    (availability) => availability.dayOfWeek === dayOfWeek,
  );

  return (
    daySchedule?.timeRanges?.some((slot) =>
      isTimeWithinAvailabilityRange(
        timeOfDayMinutes,
        {
          start: slot.startTime,
          end: slot.endTime,
        },
        dayRangeMatch,
      ),
    ) ?? false
  );
};

const isAvailableForWeeklyAvailabilityDay = ({
  weeklyScheduleOptions,
  dayOfWeek,
  timeOfDayMinutes,
  dayRangeMatch,
}: {
  weeklyScheduleOptions: WeeklyScheduleOptions | undefined;
  dayOfWeek: DayOfWeek;
  timeOfDayMinutes: number;
  dayRangeMatch: DayRangeMatchValue;
}) => {
  const daySchedule = weeklyScheduleOptions?.weeklyAvailability?.[dayOfWeek];

  if (!daySchedule?.availabilities?.length) {
    return false;
  }

  return daySchedule.availabilities.some((slot) =>
    isTimeWithinAvailabilityRange(timeOfDayMinutes, slot, dayRangeMatch),
  );
};

export const isAvailableForWeeklySchedule = ({
  weeklyScheduleOptions,
  selectedSlotStart,
  timezone,
}: {
  weeklyScheduleOptions: WeeklyScheduleOptions | undefined;
  selectedSlotStart: DateTime;
  timezone: string;
}) => {
  const slotInTimezone = selectedSlotStart.setZone(timezone);
  const timeOfDayMinutes = slotInTimezone.hour * 60 + slotInTimezone.minute;
  const currentWeekday = slotInTimezone.weekday;
  const previousWeekday = getPreviousLuxonWeekday(currentWeekday);
  const weeklyAvailability = weeklyScheduleOptions?.weeklyAvailability;

  if (!weeklyAvailability) {
    return (
      isAvailableForAvailableTimesDay({
        weeklyScheduleOptions,
        dayOfWeek: availableTimesDayByLuxonWeekday[currentWeekday],
        timeOfDayMinutes,
        dayRangeMatch: DayRangeMatch.CurrentDay,
      }) ||
      isAvailableForAvailableTimesDay({
        weeklyScheduleOptions,
        dayOfWeek: availableTimesDayByLuxonWeekday[previousWeekday],
        timeOfDayMinutes,
        dayRangeMatch: DayRangeMatch.PreviousDay,
      })
    );
  }

  return (
    isAvailableForWeeklyAvailabilityDay({
      weeklyScheduleOptions,
      dayOfWeek: weeklyAvailabilityDayByLuxonWeekday[currentWeekday],
      timeOfDayMinutes,
      dayRangeMatch: DayRangeMatch.CurrentDay,
    }) ||
    isAvailableForWeeklyAvailabilityDay({
      weeklyScheduleOptions,
      dayOfWeek: weeklyAvailabilityDayByLuxonWeekday[previousWeekday],
      timeOfDayMinutes,
      dayRangeMatch: DayRangeMatch.PreviousDay,
    })
  );
};
